import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Search, Navigation, Loader2, MapPin, BookmarkPlus, FolderPlus, ChevronDown, ChevronRight, X, Trash2, Printer, Map as MapIcon } from "lucide-react";
import MapView from "@/components/MapView";
import EchoeDetailModal from "@/components/EchoeDetailModal";
import { useMapApi, CATEGORIES, CATEGORY_COLORS } from "@/hooks/useMapApi";
import { useAuth } from "@/context/AuthContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MapPage() {
  const api = useMapApi();
  const { user } = useAuth();

  const [stories, setStories] = useState([]);
  const [bbox, setBbox] = useState(null);
  const [category, setCategory] = useState(null);
  const [activeStory, setActiveStory] = useState(null);
  const [openStory, setOpenStory] = useState(null);
  const [flyTo, setFlyTo] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchHits, setSearchHits] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [tripsOpen, setTripsOpen] = useState(false);
  const [trips, setTrips] = useState([]);
  const [activeTripId, setActiveTripId] = useState(null);

  const fetchInFlight = useRef(false);

  // Initial load — fetch all geo stories, default to US center
  const loadAll = useCallback(async () => {
    const data = await api.getStoriesInBbox({ category });
    setStories(data.stories || []);
  }, [api, category]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Load trips on mount/login
  const refreshTrips = useCallback(async () => {
    if (!user) { setTrips([]); return; }
    const t = await api.listTrips();
    setTrips(t);
  }, [api, user]);
  useEffect(() => { refreshTrips(); }, [refreshTrips]);

  // Bounds-driven refresh when map pans (only at higher zoom levels)
  const handleBoundsChange = useCallback(async (b) => {
    setBbox(b);
    if (b.zoom < 5) return; // at low zoom, keep showing all
    if (fetchInFlight.current) return;
    fetchInFlight.current = true;
    try {
      const data = await api.getStoriesInBbox({
        bbox: `${b.south},${b.west},${b.north},${b.east}`,
        category,
      });
      // Merge so we don't lose markers from the wider initial set
      if (data && data.stories) {
        setStories(prev => {
          const map = new Map();
          [...prev, ...data.stories].forEach(s => map.set(s.id, s));
          return Array.from(map.values());
        });
      }
    } finally { fetchInFlight.current = false; }
  }, [api, category]);

  // Geo search (forward geocode)
  const handleGeoSearch = useCallback(async (q) => {
    setSearchQ(q);
    if (q.length < 3) { setSearchHits([]); return; }
    setSearching(true);
    const results = await api.geocode(q);
    setSearchHits(results);
    setSearching(false);
    setShowResults(true);
  }, [api]);

  // Near me — use browser geolocation
  const handleNearMe = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      setFlyTo({ lat: latitude, lng: longitude, zoom: 12 });
      const data = await api.getNearby({ lat: latitude, lng: longitude, radius_km: 50, category });
      // merge with existing
      setStories(prev => {
        const m = new Map();
        [...prev, ...(data.stories || [])].forEach(s => {
          // serialize_story returns shape from /map/nearby; flatten loc
          const loc = s.location_coordinates;
          if (loc && loc.lat) {
            m.set(s.id, {
              id: s.id, title: s.title, category: s.category, author_name: s.author_name,
              author_id: s.author_id, original_image_url: s.original_image_url,
              favorites_count: s.favorites_count, timestamp: s.timestamp,
              lat: loc.lat, lng: loc.lng,
              address: loc.address || "", place_name: loc.place_name || "",
              is_favorited: s.is_favorited,
              distance_km: s.distance_km,
            });
          } else {
            m.set(s.id, s);
          }
        });
        return Array.from(m.values());
      });
    }, () => alert("Could not get your location"));
  }, [api, category]);

  // Filter list by current bbox (so list ↔ map stay in sync)
  const visibleStories = (() => {
    const cat = category;
    let list = stories.slice();
    if (cat) list = list.filter(s => s.category === cat);
    if (bbox && bbox.zoom >= 5) {
      list = list.filter(s => s.lat >= bbox.south && s.lat <= bbox.north && s.lng >= bbox.west && s.lng <= bbox.east);
    }
    return list;
  })();

  // Click a list item → fly map to it
  const focusStory = useCallback((s) => {
    setActiveStory(s);
    setFlyTo({ lat: s.lat, lng: s.lng, zoom: 14 });
  }, []);

  // Open the full detail modal — fetch the full story (content, media, comments) since
  // the markers carry only flattened geo metadata.
  const openStoryFull = useCallback(async (s) => {
    // Show something immediately so the modal feels responsive
    setOpenStory({ ...s, _loading: true });
    try {
      const res = await axios.get(`${API_BASE}/stories/${s.id}`);
      setOpenStory(res.data);
    } catch (err) {
      console.error("[map] failed to load full story:", err?.response?.status, err?.message);
      // Fall back to the flattened marker data — title/category still render
      setOpenStory(s);
    }
  }, []);

  return (
    <div className="bg-[#F9F7F1] min-h-[calc(100vh-56px)]">
      {/* Main content — hidden when trip detail modal is open to prevent dual-map flicker */}
      <div className={activeTripId ? "hidden" : ""}>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header bar */}
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <MapIcon size={20} className="text-[#B85C38]" />
            <h1 className="font-serif text-2xl text-[#1A1A1A]">Story Map</h1>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#718096] hidden md:inline">
              {visibleStories.length} {visibleStories.length === 1 ? "echoe" : "echoes"} visible
            </span>
          </div>

          {/* Search + Near me */}
          <div className="flex flex-1 lg:max-w-xl gap-2 items-center">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
              <input
                type="text"
                value={searchQ}
                onChange={e => handleGeoSearch(e.target.value)}
                onFocus={() => searchHits.length && setShowResults(true)}
                placeholder="Search a city, address, or landmark..."
                className="w-full bg-white border border-[#E5E2D9] pl-9 pr-9 py-2 text-sm focus:outline-none focus:border-[#B85C38]"
                data-testid="map-geo-search"
              />
              {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#718096]" />}
              {showResults && searchHits.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E2D9] shadow-lg z-50 max-h-72 overflow-y-auto" data-testid="geo-search-results">
                  {searchHits.map((h) => (
                    <button
                      key={`${h.lat},${h.lng}-${h.display_name}`}
                      onClick={() => {
                        setFlyTo({ lat: h.lat, lng: h.lng, zoom: 13 });
                        setShowResults(false);
                        setSearchQ(h.display_name);
                      }}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-[#F4F1E6] border-b border-[#E5E2D9] last:border-b-0"
                    >
                      <MapPin size={12} className="inline mr-2 text-[#B85C38]" />
                      {h.display_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleNearMe}
              className="btn-ghost text-xs whitespace-nowrap"
              data-testid="map-near-me"
              title="Find stories near my current location"
            >
              <Navigation size={14} />
              <span className="hidden sm:inline">Near Me</span>
            </button>
            {user && (
              <button
                onClick={() => setTripsOpen(o => !o)}
                className={`btn-ghost text-xs whitespace-nowrap ${tripsOpen ? "tab-active" : ""}`}
                data-testid="map-trips-toggle"
              >
                <FolderPlus size={14} />
                <span className="hidden sm:inline">My Trips</span>
                {trips.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-[#B85C38] text-white text-[10px] font-mono">
                    {trips.length}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setCategory(null)}
            className={`px-3 py-1 text-xs font-mono uppercase tracking-wider border ${category === null ? "bg-[#1A2B3C] text-white border-[#1A2B3C]" : "bg-white border-[#E5E2D9] text-[#4A5568] hover:border-[#1A2B3C]"}`}
            data-testid="filter-all"
          >
            All
          </button>
          {CATEGORIES.map(c => (
            <button
              key={c}
              onClick={() => setCategory(category === c ? null : c)}
              className={`px-3 py-1 text-xs font-mono uppercase tracking-wider border flex items-center gap-1.5 ${category === c ? "text-white border-transparent" : "bg-white border-[#E5E2D9] text-[#4A5568] hover:opacity-80"}`}
              style={category === c ? { background: CATEGORY_COLORS[c] } : {}}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: CATEGORY_COLORS[c] }} />
              {c}
            </button>
          ))}
        </div>

        {/* Split view */}
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
          {/* Story list */}
          <div className="lg:max-h-[calc(100vh-220px)] overflow-y-auto pr-1" data-testid="map-story-list">
            {visibleStories.length === 0 ? (
              <div className="bg-white border border-[#E5E2D9] p-8 text-center">
                <MapPin size={32} className="mx-auto text-[#E5E2D9] mb-3" />
                <p className="font-serif text-base text-[#718096]">No stories in this region</p>
                <p className="text-xs text-[#718096] mt-1">Zoom out or pan the map</p>
              </div>
            ) : (
              <div className="space-y-2">
                {visibleStories.map(s => (
                  <StoryListItem
                    key={s.id}
                    story={s}
                    active={activeStory?.id === s.id}
                    onFocus={() => focusStory(s)}
                    onOpen={() => openStoryFull(s)}
                    user={user}
                    trips={trips}
                    onAddToTrip={async (tripId) => {
                      const updated = await api.addStoryToTrip(tripId, s.id);
                      if (updated) refreshTrips();
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Map */}
          <div>
            <MapView
              stories={visibleStories}
              flyTo={flyTo}
              onBoundsChange={handleBoundsChange}
              onSelectStory={(s, opts) => {
                setActiveStory(s);
                if (opts?.open) openStoryFull(s);
              }}
              activeStoryId={activeStory?.id}
              height="calc(100vh - 220px)"
            />
            {/* Legend */}
            <div className="mt-2 flex flex-wrap gap-3 text-[10px] font-mono text-[#718096] uppercase tracking-wider">
              {CATEGORIES.map(c => (
                <span key={c} className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: CATEGORY_COLORS[c] }} />
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>
      {/* End main content */}

      {/* Trip panel (slide-in) */}
      {tripsOpen && user && (
        <TripsPanel
          trips={trips}
          onClose={() => setTripsOpen(false)}
          refreshTrips={refreshTrips}
          api={api}
          onOpenTrip={(id) => setActiveTripId(id)}
        />
      )}

      {/* Trip detail modal */}
      {activeTripId && (
        <TripDetailModal
          tripId={activeTripId}
          onClose={() => { setActiveTripId(null); refreshTrips(); }}
          api={api}
          onOpenEchoe={openStoryFull}
        />
      )}

      {/* Echoe full-detail modal */}
      {openStory && (
        <EchoeDetailModal
          story={openStory}
          onClose={() => setOpenStory(null)}
          canFavorite={!!user}
          canBookmark={!!user}
        />
      )}
    </div>
  );
}

// ===== Story List Item =====
function StoryListItem({ story, active, onFocus, onOpen, user, trips, onAddToTrip }) {
  const [menu, setMenu] = useState(false);
  const color = CATEGORY_COLORS[story.category] || "#1A2B3C";
  return (
    <div
      className={`bg-white border ${active ? "border-[#B85C38] shadow-md" : "border-[#E5E2D9]"} p-3 cursor-pointer hover:border-[#B85C38] transition-colors group relative`}
      onClick={onFocus}
      data-testid={`map-list-item-${story.id}`}
    >
      <div className="flex items-start gap-3">
        <span className="inline-block w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={{ background: color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color }}>{story.category}</span>
            {story.distance_km !== undefined && (
              <span className="font-mono text-[10px] text-[#718096]">{story.distance_km} km</span>
            )}
          </div>
          <h3 className="font-serif text-sm font-medium text-[#1A1A1A] leading-snug line-clamp-2">{story.title}</h3>
          {story.place_name && <p className="text-[11px] text-[#718096] mt-1 truncate">{story.place_name}</p>}
          <p className="text-[11px] text-[#718096] mt-0.5">by {story.author_name}</p>
        </div>
      </div>
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-[#F4F1E6]">
        <button
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          className="btn-ghost text-[10px] py-1 px-2 flex-1 justify-center"
          data-testid={`map-open-${story.id}`}
        >
          Open Echoe
        </button>
        {user && (
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setMenu(m => !m); }}
              className="btn-ghost text-[10px] py-1 px-2 flex items-center gap-1"
              title="Add to a trip"
              data-testid={`map-add-trip-${story.id}`}
            >
              <BookmarkPlus size={12} />
            </button>
            {menu && (
              <div className="absolute right-0 bottom-full mb-1 bg-white border border-[#E5E2D9] shadow-lg z-30 min-w-[180px]">
                {trips.length === 0 ? (
                  <p className="text-xs text-[#718096] p-3">No trips yet. Open "My Trips" to create one.</p>
                ) : (
                  trips.map(t => (
                    <button
                      key={t.id}
                      onClick={(e) => { e.stopPropagation(); onAddToTrip(t.id); setMenu(false); }}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-[#F4F1E6] flex items-center justify-between"
                    >
                      <span className="truncate">{t.name}</span>
                      {t.story_ids.includes(story.id) && <span className="text-[#5C8A4F]">✓</span>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== Trips Panel =====
function TripsPanel({ trips, onClose, refreshTrips, api, onOpenTrip }) {
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState(null);

  const create = async () => {
    const name = newName.trim();
    if (!name) {
      setMsg({ type: "error", text: "Please enter a trip name first." });
      return;
    }
    setCreating(true);
    setMsg(null);
    const created = await api.createTrip({ name });
    setCreating(false);
    if (created && created.id) {
      setNewName("");
      setMsg({ type: "success", text: `Trip "${created.name}" created! Click it below to start adding stories.` });
      refreshTrips();
      setTimeout(() => setMsg(null), 5000);
    } else {
      const err = api.error || "Could not create trip. You may need to sign in again.";
      setMsg({ type: "error", text: err });
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this trip?")) return;
    const ok = await api.deleteTrip(id);
    if (ok) {
      setMsg({ type: "success", text: "Trip deleted." });
      setTimeout(() => setMsg(null), 2500);
      refreshTrips();
    } else {
      setMsg({ type: "error", text: api.error || "Could not delete trip." });
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[400px] bg-white shadow-2xl border-l border-[#E5E2D9] z-[1100] flex flex-col" data-testid="trips-panel">
      <div className="flex items-center justify-between p-4 border-b border-[#E5E2D9]">
        <div>
          <h2 className="font-serif text-xl text-[#1A1A1A]">My Trips</h2>
          <p className="text-[11px] text-[#718096] mt-0.5">Curated story itineraries</p>
        </div>
        <button onClick={onClose} className="btn-ghost p-1.5" data-testid="close-trips-panel"><X size={16} /></button>
      </div>

      {/* Help banner */}
      <div className="px-4 py-3 bg-[#F4F1E6] border-b border-[#E5E2D9] text-[11px] text-[#4A5568] leading-relaxed">
        <strong className="text-[#1A1A1A]">How trips work:</strong> Group stories into a route you can revisit, print, or share.
        Create a trip below, then click it open to add stories from a built-in browser.
      </div>

      {/* Create */}
      <div className="p-4 border-b border-[#E5E2D9]">
        <p className="text-xs text-[#718096] mb-2 uppercase tracking-wider font-mono">Create a new trip</p>
        <div className="flex gap-2">
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && create()}
            placeholder="e.g. Cambridge Trip"
            className="flex-1 bg-white border border-[#E5E2D9] px-3 py-2 text-sm focus:outline-none focus:border-[#B85C38]"
            data-testid="new-trip-name"
            maxLength={120}
          />
          <button
            onClick={create}
            disabled={!newName.trim() || creating}
            className="btn-primary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-trip-btn"
          >
            {creating ? <Loader2 size={12} className="animate-spin" /> : "Create"}
          </button>
        </div>
        {msg && (
          <div
            className={`mt-2 text-xs px-3 py-2 border ${msg.type === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-green-50 border-green-200 text-green-700"}`}
            data-testid="trips-panel-message"
          >
            {msg.text}
          </div>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {trips.length === 0 ? (
          <div className="text-center py-8 text-[#718096]">
            <FolderPlus size={32} className="mx-auto mb-3 text-[#E5E2D9]" />
            <p className="text-sm font-medium text-[#4A5568]">No trips yet</p>
            <p className="text-xs mt-1">Type a name above and click <strong>Create</strong>.</p>
          </div>
        ) : trips.map(t => (
          <button
            key={t.id}
            onClick={() => onOpenTrip(t.id)}
            className="w-full text-left bg-white border-2 border-[#E5E2D9] p-3 hover:border-[#B85C38] hover:shadow-md transition-all group"
            data-testid={`trip-item-${t.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-base text-[#1A1A1A] truncate group-hover:text-[#B85C38] transition-colors">{t.name}</h3>
                <p className="text-xs text-[#718096] mt-1 flex items-center gap-1.5">
                  <MapPin size={11} className="inline" />
                  {t.story_ids.length} {t.story_ids.length === 1 ? "story" : "stories"}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); del(t.id); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); del(t.id); } }}
                  className="btn-ghost p-1.5 text-red-500 hover:bg-red-50 cursor-pointer"
                  title="Delete trip"
                  data-testid={`delete-trip-${t.id}`}
                >
                  <Trash2 size={12} />
                </span>
                <ChevronRight size={14} className="text-[#718096] group-hover:text-[#B85C38] transition-colors" />
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ===== Trip Detail Modal =====
function TripDetailModal({ tripId, onClose, api, onOpenEchoe }) {
  const [trip, setTrip] = useState(null);
  const [localFlyTo, setLocalFlyTo] = useState(null);
  const [allStories, setAllStories] = useState([]);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [browserCategory, setBrowserCategory] = useState(null);
  const [browserSearch, setBrowserSearch] = useState("");
  const [addBusy, setAddBusy] = useState(null);

  const load = useCallback(async () => {
    const t = await api.getTrip(tripId);
    setTrip(t);
  }, [api, tripId]);

  // Load all geo-tagged stories once for the browser
  const loadAll = useCallback(async () => {
    const data = await api.getStoriesInBbox({});
    setAllStories(data.stories || []);
  }, [api]);

  useEffect(() => { load(); loadAll(); }, [load, loadAll]);

  // ESC to close + lock body scroll
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const removeStory = async (sid) => {
    await api.removeStoryFromTrip(tripId, sid);
    load();
  };

  const addStory = async (sid) => {
    setAddBusy(sid);
    await api.addStoryToTrip(tripId, sid);
    await load();
    setAddBusy(null);
  };

  const printItinerary = () => { window.print(); };

  if (!trip) {
    return (
      <div className="fixed inset-0 bg-[#1A1A1A] z-[60] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-white" />
      </div>
    );
  }

  const tripStoryIds = new Set((trip.story_ids || []).map(String));

  // Map pins (numbered)
  const tripStories = (trip.stories || []).filter(s => s.location_coordinates && s.location_coordinates.lat).map(s => ({
    id: s.id, title: s.title, category: s.category, author_name: s.author_name,
    lat: s.location_coordinates.lat, lng: s.location_coordinates.lng,
    place_name: s.location_coordinates.place_name || "",
  }));

  const center = tripStories.length
    ? { lat: tripStories[0].lat, lng: tripStories[0].lng }
    : { lat: 39, lng: -95.5 };
  const zoom = tripStories.length ? 6 : 4;

  // Browser candidates — stories NOT already in trip
  const browserCandidates = allStories.filter(s => {
    if (tripStoryIds.has(s.id)) return false;
    if (browserCategory && s.category !== browserCategory) return false;
    if (browserSearch) {
      const q = browserSearch.toLowerCase();
      if (!(s.title.toLowerCase().includes(q) ||
            (s.place_name || "").toLowerCase().includes(q) ||
            (s.author_name || "").toLowerCase().includes(q))) return false;
    }
    return true;
  });

  return (
    <div
      className="fixed inset-0 bg-[#1A1A1A] z-[1200] overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="trip-detail-modal"
    >
      <div className="bg-[#F9F7F1] min-h-screen w-full" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E5E2D9] sticky top-0 bg-[#F9F7F1] z-20 shadow-sm print:hidden">
          <div className="min-w-0 flex-1">
            <h2 className="font-serif text-2xl text-[#1A1A1A] truncate" data-testid="trip-detail-title">{trip.name}</h2>
            {trip.description && <p className="text-xs text-[#718096] mt-1 truncate">{trip.description}</p>}
            <p className="text-xs font-mono text-[#718096] uppercase tracking-wider mt-1">{tripStories.length} stops</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setBrowserOpen(o => !o)}
              className={`btn-ghost text-xs ${browserOpen ? "tab-active" : ""}`}
              data-testid="trip-browse-stories"
              title="Browse and add stories to this trip"
            >
              <BookmarkPlus size={14} />
              <span className="hidden sm:inline">{browserOpen ? "Hide" : "Add"} Stories</span>
            </button>
            <button onClick={printItinerary} className="btn-ghost text-xs" data-testid="print-trip" title="Print / export">
              <Printer size={14} />
              <span className="hidden sm:inline">Print</span>
            </button>
            <button
              onClick={onClose}
              className="btn-primary text-xs flex items-center gap-1.5"
              data-testid="close-trip-detail"
              title="Close (Esc)"
              autoFocus
            >
              <X size={14} />
              <span>Close</span>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="max-w-[1600px] mx-auto p-4">
          {/* Empty-state guidance when no stories AND browser closed */}
          {tripStories.length === 0 && !browserOpen && (
            <div className="bg-[#F4F1E6] border-2 border-dashed border-[#B85C38] p-6 mb-4 text-center print:hidden">
              <FolderPlus size={36} className="mx-auto text-[#B85C38] mb-3" />
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-2">Your trip is empty</h3>
              <p className="text-sm text-[#4A5568] mb-4 max-w-md mx-auto">
                Add stories to build your itinerary. They'll appear as numbered pins on the map and as an ordered list you can print or export.
              </p>
              <button
                onClick={() => setBrowserOpen(true)}
                className="btn-primary text-sm"
                data-testid="trip-empty-add-cta"
              >
                <BookmarkPlus size={14} /> Add Stories Now
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Map column */}
            <div className="print:col-span-2">
              <MapView
                stories={tripStories}
                center={center}
                zoom={zoom}
                numbered
                cluster={false}
                height="500px"
                flyTo={localFlyTo}
                onSelectStory={(s, opts) => {
                  if (opts?.open) {
                    onOpenEchoe?.(s);
                  } else {
                    setLocalFlyTo({ lat: s.lat, lng: s.lng, zoom: 14 });
                  }
                }}
              />
              <p className="text-[10px] font-mono text-[#718096] uppercase tracking-wider mt-2 text-center print:hidden">
                Press <kbd className="px-1.5 py-0.5 bg-white border border-[#E5E2D9] mx-1">Esc</kbd> or click outside to close
              </p>
            </div>

            {/* Right column — current stops OR browser */}
            <div className="space-y-2">
              {!browserOpen ? (
                <>
                  <h3 className="font-mono text-xs uppercase tracking-wider text-[#718096] mb-2">Trip stops</h3>
                  {(trip.stories || []).map((s, i) => (
                    <div key={s.id} className="bg-white border border-[#E5E2D9] p-3 flex items-start gap-3" data-testid={`trip-story-${s.id}`}>
                      <button
                        onClick={() => {
                          const loc = s.location_coordinates;
                          if (loc?.lat) setLocalFlyTo({ lat: loc.lat, lng: loc.lng, zoom: 14 });
                        }}
                        className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1A2B3C] text-white font-mono text-xs flex items-center justify-center hover:bg-[#B85C38] transition-colors"
                        title="Show on map"
                      >{i + 1}</button>
                      <div className="flex-1 min-w-0">
                        <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: CATEGORY_COLORS[s.category] || "#1A2B3C" }}>{s.category}</span>
                        <h4 className="font-serif text-sm font-medium text-[#1A1A1A] line-clamp-2">{s.title}</h4>
                        {s.location_coordinates?.place_name && (
                          <p className="text-[11px] text-[#718096] mt-1">{s.location_coordinates.place_name}</p>
                        )}
                        <button
                          onClick={() => onOpenEchoe?.(s)}
                          className="mt-2 font-mono text-[10px] uppercase tracking-wider text-[#B85C38] hover:text-[#1A1A1A] print:hidden"
                          data-testid={`trip-open-echoe-${s.id}`}
                        >Open Echoe →</button>
                      </div>
                      <button onClick={() => removeStory(s.id)} className="btn-ghost p-1.5 text-red-500 print:hidden" data-testid={`trip-remove-${s.id}`} title="Remove from trip">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {tripStories.length === 0 && !browserOpen && (
                    <p className="text-sm text-[#718096] italic">Click "Add Stories" above to start.</p>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-mono text-xs uppercase tracking-wider text-[#718096]">Browse stories ({browserCandidates.length})</h3>
                    <button onClick={() => setBrowserOpen(false)} className="btn-ghost text-[10px]" data-testid="trip-browser-close">
                      Done
                    </button>
                  </div>
                  <div className="mb-2 relative">
                    <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
                    <input
                      type="text"
                      value={browserSearch}
                      onChange={e => setBrowserSearch(e.target.value)}
                      placeholder="Search by title or city..."
                      className="w-full bg-white border border-[#E5E2D9] pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:border-[#B85C38]"
                      data-testid="trip-browser-search"
                    />
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <button
                      onClick={() => setBrowserCategory(null)}
                      className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${browserCategory === null ? "bg-[#1A2B3C] text-white border-[#1A2B3C]" : "bg-white border-[#E5E2D9] text-[#4A5568]"}`}
                    >All</button>
                    {CATEGORIES.map(c => (
                      <button
                        key={c}
                        onClick={() => setBrowserCategory(browserCategory === c ? null : c)}
                        className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${browserCategory === c ? "text-white border-transparent" : "bg-white border-[#E5E2D9] text-[#4A5568]"}`}
                        style={browserCategory === c ? { background: CATEGORY_COLORS[c] } : {}}
                      >{c}</button>
                    ))}
                  </div>
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                    {browserCandidates.length === 0 ? (
                      <p className="text-sm text-[#718096] italic p-3 text-center">All matching stories are already in this trip.</p>
                    ) : browserCandidates.map(s => (
                      <div key={s.id} className="bg-white border border-[#E5E2D9] p-3 flex items-start gap-3" data-testid={`trip-browse-item-${s.id}`}>
                        <span className="inline-block w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={{ background: CATEGORY_COLORS[s.category] || "#1A2B3C" }} />
                        <div className="flex-1 min-w-0">
                          <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: CATEGORY_COLORS[s.category] || "#1A2B3C" }}>{s.category}</span>
                          <h4 className="font-serif text-sm font-medium text-[#1A1A1A] line-clamp-2">{s.title}</h4>
                          {s.place_name && <p className="text-[11px] text-[#718096] mt-1 truncate">{s.place_name}</p>}
                        </div>
                        <button
                          onClick={() => addStory(s.id)}
                          disabled={addBusy === s.id}
                          className="btn-primary text-[10px] py-1 px-2 shrink-0 disabled:opacity-50"
                          data-testid={`trip-browse-add-${s.id}`}
                        >
                          {addBusy === s.id ? <Loader2 size={10} className="animate-spin" /> : <><BookmarkPlus size={10} /> Add</>}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
